/**
 * Consistent date formatting utilities to avoid hydration mismatches
 */

/**
 * Format a date to a consistent string format
 * This avoids hydration mismatches by using the same format on server and client
 */
export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  // Use ISO date format for consistency
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${month}/${day}/${year}`;
}

/**
 * Format a date with full month name
 */
export function formatDateLong(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const year = d.getFullYear();
  const month = months[d.getMonth()];
  const day = d.getDate();

  return `${month} ${day}, ${year}`;
}

/**
 * Format a relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInMs = now.getTime() - d.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    if (diffInMinutes < 1) return 'just now';
    if (diffInMinutes === 1) return '1 minute ago';
    return `${diffInMinutes} minutes ago`;
  } else if (diffInHours < 24) {
    if (diffInHours === 1) return '1 hour ago';
    return `${diffInHours} hours ago`;
  } else if (diffInHours < 168) {
    // Less than 7 days
    const days = Math.floor(diffInHours / 24);
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  } else {
    return formatDate(d);
  }
}

/**
 * Format a date for API submission in YYYY-MM-DD format
 * This is required by the backend for date-only fields like decedentDateOfDeath
 *
 * Handles two cases:
 * 1. ISO datetime strings from backend (e.g., "2025-06-18T00:00:00.000Z") - uses UTC methods
 * 2. YYYY-MM-DD strings from forms - returns as-is to avoid timezone shifting
 *
 * @param date - Date object, string, or null/undefined
 * @returns Date string in YYYY-MM-DD format, or undefined if input is null/undefined
 *
 * @example
 * formatDateForApi('2025-06-18') // '2025-06-18' (already correct format)
 * formatDateForApi('2025-06-18T00:00:00.000Z') // '2025-06-18' (extracts date from ISO)
 * formatDateForApi(new Date('2025-06-18T00:00:00.000Z')) // '2025-06-18'
 * formatDateForApi(undefined) // undefined
 */
export function formatDateForApi(
  date: Date | string | null | undefined
): string | undefined {
  if (!date) return undefined;

  // If it's already a YYYY-MM-DD string, return it as-is to avoid timezone shifting
  // This happens when dates come from DatePicker components
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const d = typeof date === 'string' ? new Date(date) : date;

  // Validate date
  if (isNaN(d.getTime())) {
    console.warn('formatDateForApi: Invalid date provided', date);
    return undefined;
  }

  // Use UTC methods to extract date from ISO datetime strings
  // This is correct because backend sends dates as UTC (e.g., "2025-06-18T00:00:00.000Z")
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to YYYY-MM-DD string using LOCAL timezone
 * Use this for date picker selections to avoid timezone shifting bugs
 *
 * @param date - Date object from date picker
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * // User in Sydney (UTC+11) selects Jan 15, 2025
 * const date = new Date(2025, 0, 15); // Jan 15, 2025 local time
 * formatDateToLocal(date) // '2025-01-15' (correct!)
 *
 * // If we used toISOString() instead:
 * date.toISOString() // '2025-01-14T13:00:00.000Z' (wrong day!)
 */
export function formatDateToLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Convert a date-picker selection to a UTC-midnight ISO instant whose UTC
 * calendar date equals the picked calendar date.
 *
 * Use this for a calendar-date value stored in a `timestamptz` column that the
 * backend reads back by extracting the UTC date (`.toISOString().split('T')[0]`),
 * e.g. `matter.advertisedAt` (surfaced as the grant stage's `publishedDate`). A
 * DatePicker yields a Date at LOCAL midnight, so `date.toISOString()` shifts a
 * positive-offset selection (e.g. AEST) back to the previous UTC day — the
 * recorded date then round-trips and displays as the day before. Anchoring to
 * UTC midnight of the picked Y/M/D keeps every UTC-based read stable regardless
 * of the DB session timezone.
 *
 * For a `date`-typed column (date-only, no instant) use `formatDateToLocal`.
 *
 * @param date - Date object from a date picker (local midnight)
 * @returns ISO 8601 instant at UTC midnight of the picked date, e.g. '2026-07-22T00:00:00.000Z'
 *
 * @example
 * // User in Sydney (UTC+10) selects 22 July 2026
 * const date = new Date(2026, 6, 22); // 22 Jul 2026 local time
 * toUtcDateOnlyIso(date) // '2026-07-22T00:00:00.000Z' (correct — same day)
 *
 * // If we used toISOString() instead:
 * date.toISOString() // '2026-07-21T14:00:00.000Z' (wrong day!)
 */
export function toUtcDateOnlyIso(date: Date): string {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  ).toISOString();
}
